/* eslint-disable @typescript-eslint/no-explicit-any */
import DataTable from "@/components/data-table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGetDeliveryErrorData } from "@/hooks/use-postmaster";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { type ColDef } from "ag-grid-community";
import { format } from "date-fns";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { useMemo } from "react";
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export interface DeliveryError {
  errorType: string;
  reason: string;
  percentage: number;
}

function DeliveryErrorTabContent({
  date,
  domain,
}: {
  date: string;
  domain: string;
}) {
  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    const start = new Date();

    switch (date) {
      case "last7days":
        start.setDate(end.getDate() - 7);
        break;
      case "last30days":
        start.setDate(end.getDate() - 30);
        break;
      case "last60days":
        start.setDate(end.getDate() - 60);
        break;
      case "last90days":
        start.setDate(end.getDate() - 90);
        break;
      case "last120days":
        start.setDate(end.getDate() - 120);
        break;
      default:
        start.setDate(end.getDate() - 7);
    }

    return { startDate: start, endDate: end };
  }, [date]);

  const { data, isLoading } = useGetDeliveryErrorData({
    domain,
    startDate,
    endDate,
  });

  const columnDefs: ColDef[] = [
    {
      field: "date",
      headerName: "Date",
      filter: true,
      sortable: true,
      valueFormatter: (params) =>
        format(new Date(params.value), "MMM dd, yyyy"),
    },
    {
      field: "error",
      headerName: "Delivery error rate",
      filter: true,
      sortable: true,
      valueFormatter: (params) => {
        const value = params.value || 0;
        return value === 0 ? "0%" : `${value.toFixed(2)}%`;
      },
      cellRenderer: (params: any) => {
        const errorValue = params.value || 0;
        return (
          <div className="text-right">
            {errorValue === 0 ? "0%" : `${errorValue.toFixed(2)}%`}
          </div>
        );
      },
    },
    // {
    //   field: "deliveryErrors",
    //   headerName: "Error Types",
    //   sortable: false,
    //   filter: false,
    //   cellRenderer: (params: any) => {
    //     const errors: DeliveryError[] = params.value || [];
    //     if (errors.length === 0) {
    //       return <span className="text-xs text-muted-foreground">No errors</span>;
    //     }
    //     return (
    //       <div className="flex flex-wrap gap-1 items-center h-full">
    //         {errors.map((error, index) => {
    //           // Determine badge color based on errorType
    //           const getBadgeClass = (errorType: string) => {
    //             if (errorType === "Temp fail" || errorType === "RATE_LIMIT_EXCEEDED") {
    //               return "bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-400";
    //             }
    //             if (errorType === "Reject") {
    //               return "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-400";
    //             }
    //             return "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-900/20 dark:text-slate-400 dark:border-slate-400";
    //           };
              
    //           return (
    //             <Badge
    //               key={index}
    //               variant="outline"
    //               className={getBadgeClass(error.errorType)}
    //             >
    //               {error.errorType}
    //             </Badge>
    //           );
    //         })}
    //       </div>
    //     );
    //   },
    // },
  ];

  const formattedChartData = data?.chartData.map((item) => ({
    ...item,
    date: format(new Date(item.date), "MMM dd, yyyy"),
    error: item.error || 0,
  }));

  // Calculate dynamic domain for Y-axis based on max error value
  const maxError = useMemo(() => {
    if (!data?.chartData || data.chartData.length === 0) return 100;
    const max = Math.max(...data.chartData.map((item) => item.error || 0));
    // Round up to nearest 10, with minimum of 10
    return Math.max(10, Math.ceil(max / 10) * 10);
  }, [data?.chartData]);

  if (isLoading)
    return (
      <div className="flex justify-center items-center h-[50vh] w-full text-foreground">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">
              Current Error Rate
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">
                  {data?.statistics.currentError === 0 
                    ? "0%" 
                    : `${data?.statistics.currentError.toFixed(2)}%`}
                </div>
                <p className="text-xs text-muted-foreground">Last 24 hours</p>
              </div>
              <CheckCircle2
                className={`h-5 w-5 ${
                  data?.statistics.currentError &&
                  data?.statistics.currentError <= data?.statistics.threshold
                    ? "text-green-500"
                    : "text-yellow-500"
                }`}
              />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">
              Average Error Rate
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">
                  {data?.statistics.averageError === 0 
                    ? "0%" 
                    : `${data?.statistics.averageError.toFixed(2)}%`}
                </div>
                <p className="text-xs text-muted-foreground">
                  {data?.statistics.monitoringPeriod}
                </p>
              </div>
              <CheckCircle2
                className={`h-5 w-5 ${
                  data?.statistics.averageError &&
                  data?.statistics.averageError <= data?.statistics.threshold
                    ? "text-green-500"
                    : "text-yellow-500"
                }`}
              />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">
              Error Threshold
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">
                  {data?.statistics.threshold === 0 
                    ? "0%" 
                    : `${data?.statistics.threshold.toFixed(2)}%`}
                </div>
                <p className="text-xs text-muted-foreground">Warning level</p>
              </div>
              <AlertTriangle className="h-5 w-5 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex items-center gap-2">
              <Badge
                className={`shadow-none ${
                  data?.statistics.status === "Healthy"
                    ? "bg-green-100 text-green-700"
                    : "bg-yellow-100 text-yellow-700"
                } hover:bg-opacity-80`}
              >
                {data?.statistics.status}
              </Badge>
              <span className="text-sm text-muted-foreground">
                {data?.statistics.status === "Healthy"
                  ? "No issues detected"
                  : "Above threshold"}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="p-4">
          <CardTitle>Delivery Errors</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={formattedChartData}
                margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
              >
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12 }}
                  tickMargin={10}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  domain={[0, maxError]}
                  tickFormatter={(value) => `${value}%`}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--background))",
                    border: "1px solid #e5e7eb",
                    padding: "8px",
                  }}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const chartData = payload[0].payload;
                      const getBadgeClass = (errorType: string) => {
                        if (errorType === "Temp fail" || errorType === "RATE_LIMIT_EXCEEDED") {
                          return "bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400";
                        }
                        if (errorType === "Reject") {
                          return "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400";
                        }
                        return "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-900/20 dark:text-slate-400";
                      };
                      
                      return (
                        <div className="p-2 border rounded-lg shadow-sm bg-background">
                          <p className="font-medium mb-1">Date: {label}</p>
                          <p className="mb-2">Error Rate: {(chartData.error || 0).toFixed(2)}%</p>
                          {chartData.deliveryErrors && chartData.deliveryErrors.length > 0 && (
                            <div className="flex flex-col gap-1 mt-2">
                              {chartData.deliveryErrors.map(
                                (error: DeliveryError, index: number) => (
                                  <div key={index} className="text-xs">
                                    <Badge
                                      variant="outline"
                                      className={getBadgeClass(error.errorType)}
                                    >
                                      {error.errorType}
                                    </Badge>
                                    <span className="ml-2 text-muted-foreground">
                                      {error.reason} ({(error.percentage || 0).toFixed(2)}%)
                                    </span>
                                  </div>
                                )
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="error"
                  name="Delivery error rate"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  dot={{ fill: "#3B82F6", strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
      <div className="mt-6">
        <DataTable
          rowData={data?.chartData || []}
          columnDefs={columnDefs}
          height={400}
        />
      </div>
    </div>
  );
}

// Export both the component for use in other files and the Route for the router
export { DeliveryErrorTabContent };

function DeliveryErrorTabRouteComponent() {
  // Get domain and date from search params if available, otherwise use defaults
  const search = useSearch({ strict: false });
  const domain = (search as { domain?: string })?.domain || "";
  const date = (search as { date?: string })?.date || "last7days";
  return <DeliveryErrorTabContent date={date} domain={domain} />;
}

export const Route = createFileRoute(
  "/_private/postmaster/tabs/delivery-error-tab"
)({
  component: DeliveryErrorTabRouteComponent,
});
